import {
  Component,
  EventEmitter,
  Output,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import * as ibantools from 'ibantools';
import {
  CompanySearchBankAccountItem,
  CompanySearchService,
} from '../company-search-service/company-search.service';

@Component({
  selector: 'payee-company-search-bank-account-picker',
  templateUrl: './payee-company-search-bank-account-picker.component.html',
  styleUrls: ['./payee-company-search-bank-account-picker.component.scss'],
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MatProgressSpinner,
  ],
})
export class PayeeCompanySearchBankAccountPickerComponent {
  private companySearchService = inject(CompanySearchService);

  readonly taxNumber = input<string | null | undefined>(null);
  readonly matchAccountNumber = input<string | null | undefined>(null);

  readonly taxRoot = computed(() =>
    (this.taxNumber() ?? '').replace(/\D/g, '').slice(0, 8),
  );

  @Output() accountSelected = new EventEmitter<CompanySearchBankAccountItem>();

  companySearchBankAccounts: CompanySearchBankAccountItem[] = [];
  companySearchLoading = false;
  readonly selectedAccountKey = new FormControl<string | null>(null);

  private requestId = 0;

  constructor() {
    effect(() => {
      const taxRoot = this.taxRoot();
      if (taxRoot.length < 8) {
        this.resetPicker();
        return;
      }

      void this.fetchCompanySearchBankAccounts(taxRoot);
    });

    effect(() => {
      this.matchAccountNumber();
      this.preselectMatchingAccount();
    });
  }

  accountKey(account: CompanySearchBankAccountItem): string {
    return (
      this.normalizeAccountIdentifier(account.iban)
      ?? this.normalizeAccountIdentifier(account.account_number)
      ?? ''
    );
  }

  formatCompanySearchAccountNumber(
    account: CompanySearchBankAccountItem,
  ): string {
    return account.iban || account.account_number || '';
  }

  selectCompanySearchAccount(account: CompanySearchBankAccountItem): void {
    const key = this.accountKey(account);
    this.selectedAccountKey.setValue(key);
    this.accountSelected.emit(account);
  }

  isCompanySearchAccountSelected(account: CompanySearchBankAccountItem): boolean {
    return this.selectedAccountKey.value === this.accountKey(account);
  }

  private async fetchCompanySearchBankAccounts(taxRoot: string): Promise<void> {
    const requestId = ++this.requestId;

    this.companySearchBankAccounts = [];
    this.selectedAccountKey.setValue(null, { emitEvent: false });
    this.companySearchLoading = true;
    try {
      const detailed = await this.companySearchService.detailedSearch(taxRoot);
      if (this.isStaleRequest(requestId, taxRoot)) {
        return;
      }

      if (!detailed?.company) {
        return;
      }

      this.companySearchBankAccounts = detailed.company.bankAccounts.filter(
        (account) => this.accountKey(account) !== '',
      );
      if (this.companySearchBankAccounts.length > 0) {
        this.preselectMatchingAccount();
      }
    } catch {
      if (this.isStaleRequest(requestId, taxRoot)) {
        return;
      }
    } finally {
      if (requestId === this.requestId) {
        this.companySearchLoading = false;
      }
    }
  }

  private isStaleRequest(requestId: number, taxRoot: string): boolean {
    return requestId !== this.requestId || this.taxRoot() !== taxRoot;
  }

  private resetPicker(): void {
    this.requestId += 1;
    this.companySearchBankAccounts = [];
    this.companySearchLoading = false;
    this.selectedAccountKey.setValue(null, { emitEvent: false });
  }

  private preselectMatchingAccount(): void {
    const savedKey = this.normalizeAccountIdentifier(this.matchAccountNumber());
    if (!savedKey) {
      return;
    }

    const match = this.companySearchBankAccounts.find(
      (account) => this.accountKey(account) === savedKey,
    );
    if (!match || this.selectedAccountKey.value === savedKey) {
      return;
    }

    this.selectedAccountKey.setValue(savedKey, { emitEvent: false });
  }

  private normalizeAccountIdentifier(
    value: string | null | undefined,
  ): string | null {
    if (!value) {
      return null;
    }

    const electronic = ibantools.electronicFormatIBAN(value);
    if (electronic) {
      return electronic;
    }

    const digits = value.replace(/\D/g, '');
    return digits || null;
  }
}
